import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { useAuth } from "@/lib/AuthContext";
import { changePassword } from "@/api/authApi";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  User,
  Settings,
  ShoppingCart,
  Shield,
  Crown,
  Coins,
  Lock,
  Save,
  Check,
  Globe,
  Gift,
  UserPlus,
  Video,
  Youtube,
  Eye,
  EyeOff,
  AlertCircle,
  Calendar,
  Activity,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(val) {
  if (!val) return '—';
  return new Date(val).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function accountStatusLabel(status) {
  const map = { 1: 'Registered', 5: 'Active' };
  return map[status] ?? `Status ${status}`;
}

function worldLabel(worldId) {
  if (worldId === -1 || worldId == null) return 'Not in game';
  return `World ${worldId}`;
}

// ---------------------------------------------------------------------------
// Sub-component: read-only info row
// ---------------------------------------------------------------------------
function InfoRow({ label, value }) {
  return (
    <div className="space-y-1">
      <Label className="text-gray-500 text-xs uppercase tracking-wider">{label}</Label>
      <p className="text-white font-medium">{value ?? '—'}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------
export default function Dashboard() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoadingAuth, logout } = useAuth();

  // Change-password form state
  const [currentPassword, setCurrentPassword]   = useState('');
  const [newPassword, setNewPassword]           = useState('');
  const [confirmPassword, setConfirmPassword]   = useState('');
  const [showPasswords, setShowPasswords]       = useState(false);
  const [pwLoading, setPwLoading]               = useState(false);
  const [pwMessage, setPwMessage]               = useState(null); // { type: 'success'|'error', text }

  // Redirect if not logged in (after auth is resolved)
  if (!isLoadingAuth && !isAuthenticated) {
    navigate(createPageUrl("Login"));
    return null;
  }

  if (isLoadingAuth || !user) {
    return (
      <div className="min-h-screen relative">
        <div className="fixed inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#1a0a1a]/95 to-[#0a0a0a]" />
          <div className="absolute inset-0 bg-[url('https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/6915ad56fe64ad9662a36d0b/c5a838e31_loading_screen_12.png')] bg-cover bg-center opacity-20" />
        </div>
        <div className="relative z-10 flex items-center justify-center min-h-screen">
          <div className="animate-spin w-12 h-12 border-4 border-[#8B0000] border-t-white rounded-full" />
        </div>
      </div>
    );
  }

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwMessage(null);

    if (newPassword !== confirmPassword) {
      return setPwMessage({ type: 'error', text: 'New passwords do not match.' });
    }
    if (newPassword.length < 8) {
      return setPwMessage({ type: 'error', text: 'New password must be at least 8 characters.' });
    }

    setPwLoading(true);
    try {
      await changePassword({ currentPassword, newPassword });
      setPwMessage({ type: 'success', text: 'Password updated successfully.' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPwMessage({ type: 'error', text: err.message || 'Failed to update password.' });
    } finally {
      setPwLoading(false);
    }
  };

  const referralCode = `KA-${String(user.id).padStart(6, '0')}`;

  return (
    <div className="min-h-screen relative">
      {/* Background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#1a0a1a]/95 to-[#0a0a0a]" />
        <div className="absolute inset-0 bg-[url('https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/6915ad56fe64ad9662a36d0b/c5a838e31_loading_screen_12.png')] bg-cover bg-center opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/80 via-transparent to-[#0a0a0a]/90" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,#0a0a0a_100%)]" />
      </div>

      <div className="relative z-10 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Header */}
          <div className="mb-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-1">
                Welcome, {user.username}
              </h1>
              <p className="text-gray-400">Manage your account and prepare for battle</p>
            </div>
            <div className="flex items-center gap-3">
              <Badge className={`px-3 py-1 text-sm border ${
                user.status === 5
                  ? 'bg-green-900/20 text-green-400 border-green-400/30'
                  : 'bg-yellow-900/20 text-yellow-400 border-yellow-400/30'
              }`}>
                <Activity className="w-3 h-3 mr-1" />
                {accountStatusLabel(user.status)}
              </Badge>
              {user.suspended === 1 && (
                <Badge className="bg-red-900/20 text-red-400 border-red-400/30 px-3 py-1">
                  Suspended
                </Badge>
              )}
            </div>
          </div>

          {/* Stats cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            <Card className="bg-gradient-to-b from-[#1a1a1a]/80 to-[#0a0a0a]/80 backdrop-blur-sm border-[#8B0000]/30 p-6">
              <Coins className="w-7 h-7 text-white mb-2" />
              <div className="text-2xl font-bold text-white">{user.coin_current ?? 0}</div>
              <div className="text-sm text-gray-400">Current Coins</div>
            </Card>

            <Card className="bg-gradient-to-b from-[#1a1a1a]/80 to-[#0a0a0a]/80 backdrop-blur-sm border-[#8B0000]/30 p-6">
              <Crown className="w-7 h-7 text-white mb-2" />
              <div className="text-2xl font-bold text-white">{user.coin_total ?? 0}</div>
              <div className="text-sm text-gray-400">Total Coins Earned</div>
            </Card>

            <Card className="bg-gradient-to-b from-[#1a1a1a]/80 to-[#0a0a0a]/80 backdrop-blur-sm border-[#8B0000]/30 p-6">
              <Globe className="w-7 h-7 text-white mb-2" />
              <div className="text-lg font-bold text-white truncate">{worldLabel(user.current_world_id)}</div>
              <div className="text-sm text-gray-400">Current World</div>
            </Card>

            <Card className="bg-gradient-to-b from-[#1a1a1a]/80 to-[#0a0a0a]/80 backdrop-blur-sm border-[#8B0000]/30 p-6">
              <Calendar className="w-7 h-7 text-white mb-2" />
              <div className="text-lg font-bold text-white">{formatDate(user.created_at)}</div>
              <div className="text-sm text-gray-400">Member Since</div>
            </Card>
          </div>

          {/* Alpha notice */}
          <Card className="mb-10 bg-gradient-to-b from-[#1a1a1a]/80 to-[#0a0a0a]/80 backdrop-blur-sm border-[#8B0000]/30 p-8">
            <div className="text-center">
              <Crown className="w-12 h-12 text-white mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">Alpha Testing Program</h3>
              <p className="text-gray-400 mb-6 max-w-2xl mx-auto">
                We'll be selecting alpha testers from our registered users in Q2 2026.
                Apply on Discord to increase your chances!
              </p>
              <Button
                variant="outline"
                className="border-white text-white hover:bg-white/10 hover:text-white hover:border-white"
                onClick={() => window.open('https://discord.gg/knightarena', '_blank')}
              >
                Apply on Discord
              </Button>
            </div>
          </Card>

          {/* Tabs */}
          <Tabs defaultValue="account" className="w-full">
            <TabsList className="grid w-full md:w-auto md:inline-grid grid-cols-3 bg-[#1a1a1a]/80 backdrop-blur-sm border border-[#8B0000]/30 mb-8">
              <TabsTrigger value="account" className="data-[state=active]:bg-[#8B0000] data-[state=active]:text-white">
                <User className="w-4 h-4 mr-2" />
                Account
              </TabsTrigger>
              <TabsTrigger value="earn-credits" className="data-[state=active]:bg-[#8B0000] data-[state=active]:text-white">
                <Gift className="w-4 h-4 mr-2" />
                Earn Credits
              </TabsTrigger>
              <TabsTrigger value="store" className="data-[state=active]:bg-[#8B0000] data-[state=active]:text-white">
                <ShoppingCart className="w-4 h-4 mr-2" />
                Store
              </TabsTrigger>
            </TabsList>

            {/* ----------------------------------------------------------------
                Account tab
            ---------------------------------------------------------------- */}
            <TabsContent value="account">
              <div className="space-y-6">

                {/* Account info */}
                <Card className="bg-gradient-to-b from-[#1a1a1a]/80 to-[#0a0a0a]/80 backdrop-blur-sm border-[#8B0000]/30 p-8">
                  <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Account Information
                  </h2>

                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    <InfoRow label="Account ID" value={`#${user.id}`} />
                    <InfoRow label="Username" value={user.username} />
                    <InfoRow label="Email" value={user.email} />
                    <InfoRow label="Date of Birth" value={formatDate(user.birthdate)} />
                    <InfoRow label="Status" value={accountStatusLabel(user.status)} />
                    <InfoRow label="Activated" value={user.activated ? 'Yes' : 'No'} />
                    <InfoRow label="Current World" value={worldLabel(user.current_world_id)} />
                    <InfoRow label="Member Since" value={formatDate(user.created_at)} />
                    <InfoRow label="Last Active" value={formatDate(user.last_modified_at)} />
                  </div>
                </Card>

                {/* Coin summary */}
                <Card className="bg-gradient-to-b from-[#1a1a1a]/80 to-[#0a0a0a]/80 backdrop-blur-sm border-[#8B0000]/30 p-8">
                  <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                    <Coins className="w-5 h-5" />
                    Coin Balance
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="bg-[#0a0a0a]/50 border border-[#8B0000]/30 rounded-lg p-5">
                      <p className="text-gray-400 text-sm mb-1">Current Balance</p>
                      <p className="text-3xl font-bold text-white">{user.coin_current ?? 0}</p>
                    </div>
                    <div className="bg-[#0a0a0a]/50 border border-[#8B0000]/30 rounded-lg p-5">
                      <p className="text-gray-400 text-sm mb-1">Total Earned (All Time)</p>
                      <p className="text-3xl font-bold text-white">{user.coin_total ?? 0}</p>
                    </div>
                  </div>
                </Card>

                {/* Change password */}
                <Card className="bg-gradient-to-b from-[#1a1a1a]/80 to-[#0a0a0a]/80 backdrop-blur-sm border-[#8B0000]/30 p-8">
                  <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                    <Lock className="w-5 h-5" />
                    Change Password
                  </h2>

                  {pwMessage && (
                    <Alert className={`mb-5 ${
                      pwMessage.type === 'success'
                        ? 'bg-green-900/20 border-green-500/50'
                        : 'bg-red-900/20 border-red-500/50'
                    }`}>
                      {pwMessage.type === 'success'
                        ? <Check className="h-4 w-4 text-green-500" />
                        : <AlertCircle className="h-4 w-4 text-red-400" />
                      }
                      <AlertDescription className={pwMessage.type === 'success' ? 'text-green-400' : 'text-red-400'}>
                        {pwMessage.text}
                      </AlertDescription>
                    </Alert>
                  )}

                  <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
                    <div className="space-y-2">
                      <Label htmlFor="currentPassword" className="text-gray-300">Current Password</Label>
                      <div className="relative">
                        <Input
                          id="currentPassword"
                          type={showPasswords ? 'text' : 'password'}
                          autoComplete="current-password"
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          required
                          placeholder="••••••••"
                          className="bg-white/5 border-[#8B0000]/40 text-white placeholder:text-gray-600 focus:border-white/50 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords(!showPasswords)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                        >
                          {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="newPassword" className="text-gray-300">New Password</Label>
                      <Input
                        id="newPassword"
                        type={showPasswords ? 'text' : 'password'}
                        autoComplete="new-password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        placeholder="At least 8 characters"
                        className="bg-white/5 border-[#8B0000]/40 text-white placeholder:text-gray-600 focus:border-white/50"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword" className="text-gray-300">Confirm New Password</Label>
                      <Input
                        id="confirmPassword"
                        type={showPasswords ? 'text' : 'password'}
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        placeholder="Repeat new password"
                        className="bg-white/5 border-[#8B0000]/40 text-white placeholder:text-gray-600 focus:border-white/50"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={pwLoading}
                      className="bg-gradient-to-r from-[#8B0000] to-[#B22222] hover:from-[#B22222] hover:to-[#8B0000] text-white hover:text-white border-none"
                    >
                      {pwLoading ? (
                        <span className="flex items-center gap-2">
                          <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                          Saving...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Save className="w-4 h-4" />
                          Update Password
                        </span>
                      )}
                    </Button>
                  </form>
                </Card>
              </div>
            </TabsContent>

            {/* ----------------------------------------------------------------
                Earn Credits tab
            ---------------------------------------------------------------- */}
            <TabsContent value="earn-credits">
              <Card className="bg-gradient-to-b from-[#1a1a1a]/80 to-[#0a0a0a]/80 backdrop-blur-sm border-[#8B0000]/30 p-8">
                <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
                  <Gift className="w-6 h-6" />
                  Earn Free Coins
                </h2>
                <p className="text-gray-400 mb-8">
                  Complete these tasks to earn free coins for cosmetic items. All rewards are fair and encourage community growth!
                </p>

                <div className="space-y-6">

                  {/* Invite a Friend */}
                  <Card className="bg-[#0a0a0a]/60 border-[#8B0000]/30 p-6 hover:border-white/30 transition-all">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#8B0000] to-[#B22222] rounded-lg flex items-center justify-center flex-shrink-0">
                        <UserPlus className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white mb-2">Invite a Friend</h3>
                        <p className="text-gray-400 mb-4">
                          Share your referral code. Earn <span className="text-white font-bold">500 coins</span> when a friend registers and reaches level 20 in-game.
                        </p>
                        <div className="flex items-center gap-3 max-w-sm">
                          <Input
                            value={referralCode}
                            readOnly
                            className="bg-[#0a0a0a] border-[#8B0000]/30 text-white font-mono"
                          />
                          <Button
                            variant="outline"
                            className="border-white text-white hover:bg-white/10 hover:text-white hover:border-white flex-shrink-0"
                            onClick={() => navigator.clipboard.writeText(referralCode)}
                          >
                            Copy
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>

                  {/* Twitch */}
                  <Card className="bg-[#0a0a0a]/60 border-[#8B0000]/30 p-6 hover:border-white/30 transition-all">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-purple-800 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Video className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white mb-2">Stream on Twitch</h3>
                        <p className="text-gray-400 mb-4">
                          Stream Knight Arena for at least 2 hours. Earn <span className="text-white font-bold">1,000 coins</span> per approved stream.
                        </p>
                        <Button
                          className="bg-purple-600 hover:bg-purple-700 text-white border-none"
                          onClick={() => window.open('https://discord.gg/knightarena', '_blank')}
                        >
                          <Video className="w-4 h-4 mr-2" />
                          Submit via Discord
                        </Button>
                      </div>
                    </div>
                  </Card>

                  {/* YouTube */}
                  <Card className="bg-[#0a0a0a]/60 border-[#8B0000]/30 p-6 hover:border-white/30 transition-all">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-red-800 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Youtube className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white mb-2">Post on YouTube</h3>
                        <p className="text-gray-400 mb-4">
                          Create a video about Knight Arena with at least 1,000 views. Earn <span className="text-white font-bold">2,000 coins</span> per approved video.
                        </p>
                        <Button
                          className="bg-red-600 hover:bg-red-700 text-white border-none"
                          onClick={() => window.open('https://discord.gg/knightarena', '_blank')}
                        >
                          <Youtube className="w-4 h-4 mr-2" />
                          Submit via Discord
                        </Button>
                      </div>
                    </div>
                  </Card>
                </div>

                <div className="mt-8 p-4 bg-[#0a0a0a]/50 border border-[#8B0000]/30 rounded-lg">
                  <p className="text-sm text-gray-400">
                    <strong className="text-white">Note:</strong> All submissions are manually reviewed.
                    Coins are awarded within 48 hours of verification. Abuse of the system will result in account suspension.
                  </p>
                </div>
              </Card>
            </TabsContent>

            {/* ----------------------------------------------------------------
                Store tab
            ---------------------------------------------------------------- */}
            <TabsContent value="store">
              <Card className="bg-gradient-to-b from-[#1a1a1a]/80 to-[#0a0a0a]/80 backdrop-blur-sm border-[#8B0000]/30 p-8">
                <div className="flex items-center justify-between mb-8">
                  <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                    <ShoppingCart className="w-6 h-6" />
                    Store
                  </h2>
                  <div className="flex items-center gap-2 text-white font-bold text-xl">
                    <Coins className="w-5 h-5" />
                    {user.coin_current ?? 0}
                    <span className="text-sm font-normal text-gray-400 ml-1">coins</span>
                  </div>
                </div>

                <div className="text-center py-16">
                  <ShoppingCart className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-400 mb-2">Store Coming Soon</h3>
                  <p className="text-gray-500 max-w-sm mx-auto">
                    Cosmetic items will be available once the game launches. Keep earning coins now!
                  </p>
                </div>
              </Card>
            </TabsContent>

          </Tabs>
        </div>
      </div>
    </div>
  );
}
